import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import type { IneligibilityReason } from './resolve-invite-on-publish';
import { INVITE_ON_PAGE_PUBLISH_EXPERIMENT_KEY, getExperimentCohort } from './feature-flag';

export type EVENT_TYPE = 'sendTrackEvent' | 'sendOperationalEvent' | 'sendUIEvent';

type FireExperimentAnalytics = (
	type: EVENT_TYPE,
	actionSubject: string,
	action: string,
	actionSubjectId?: string,
	attributes?: Record<string, any>,
) => unknown;

export const getExperimentAnalytics =
	(
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		{ attributes: commonAttributes = {} } = {},
	): FireExperimentAnalytics =>
	(type, actionSubject, action, actionSubjectId, attributes) =>
		createAnalyticsEvent({
			type,
			data: {
				action,
				actionSubject,
				actionSubjectId,
				source: 'inviteOnPagePublish',
				attributes: {
					...commonAttributes,
					...attributes,
				},
			},
		}).fire();

export const fireEligibilityChecked = (
	analytics: FireExperimentAnalytics,
	ineligibilityReason: IneligibilityReason | 'ELIGIBLE',
) =>
	analytics('sendTrackEvent', 'eligibility', 'checked', undefined, {
		flagKey: INVITE_ON_PAGE_PUBLISH_EXPERIMENT_KEY,
		ineligibilityReason,
	});

export const fireExperimentError = (analytics: FireExperimentAnalytics, error: Error) =>
	analytics('sendOperationalEvent', 'showInviteOnPagePublishFlag', 'failed', undefined, {
		message: String(error),
		details: { ...(error || {}) },
	});

export const fireFeatureExposed = (analytics: FireExperimentAnalytics) => {
	const cohort = getExperimentCohort({ fireExposure: true });
	analytics('sendTrackEvent', 'feature', 'exposed', undefined, {
		flagKey: INVITE_ON_PAGE_PUBLISH_EXPERIMENT_KEY,
		cohort,
	});
};

export const fireFlagDismissed = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'flag', 'dismissed', 'inviteOnPagePublishFlag');

export const fireUserOptedOut = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'user', 'optedOut', 'inviteOnPagePublishFlag');

export const fireFlagShown = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'flag', 'shown', 'inviteOnPagePublishFlag');

export const fireAddButtonClicked = (analytics: FireExperimentAnalytics) =>
	analytics('sendUIEvent', 'button', 'clicked', 'inviteOnPagePublishFlag');

export const fireUserInvited = (analytics: FireExperimentAnalytics, inviteeId: string) =>
	analytics('sendTrackEvent', 'user', 'invited', inviteeId);

export const fireProductAccessRequested = (analytics: FireExperimentAnalytics, inviteeId: string) =>
	analytics('sendTrackEvent', 'productAccess', 'requested', inviteeId);

export const fireInviteRequested = (analytics: FireExperimentAnalytics) =>
	analytics('sendTrackEvent', 'invite', 'requested', undefined);

export const fireInviteSucceeded = (analytics: FireExperimentAnalytics) =>
	analytics('sendOperationalEvent', 'invite', 'succeeded', undefined);

export const fireInviteFailed = (analytics: FireExperimentAnalytics, error: Error) =>
	analytics('sendOperationalEvent', 'invite', 'failed', undefined, {
		message: String(error),
		details: { ...(error || {}) },
	});
